import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useChat } from '@/contexts/ChatContext';
import { useToast } from '@/hooks/use-toast';
import { Forward, Hash, Search, Check } from 'lucide-react';

interface ForwardMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: any | null;
}

type ForwardTarget =
  | { kind: 'team-room'; id: string; roomId: string; label: string }
  | { kind: 'dm'; id: string; userId: string; label: string; email?: string; avatarUrl?: string };

function getInitials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function ForwardMessageDialog({ open, onOpenChange, message }: ForwardMessageDialogProps) {
  const chat = useChat();
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);

  const currentUser = chat?.currentUser;
  const teamMembers = chat?.teamMembers || [];

  const teamRoomId = currentUser?.organization_id
    ? `org_${currentUser.organization_id}_team_general`
    : 'team-general';

  const targets = useMemo<ForwardTarget[]>(() => {
    const list: ForwardTarget[] = [
      { kind: 'team-room', id: `room:${teamRoomId}`, roomId: teamRoomId, label: 'Team Chat (#general)' },
    ];
    for (const m of teamMembers) {
      if (!currentUser || m.id === currentUser.id) continue;
      list.push({
        kind: 'dm',
        id: `dm:${m.id}`,
        userId: m.id,
        label: m.full_name || m.email || 'Unknown',
        email: m.email,
        avatarUrl: m.avatar_url,
      });
    }
    return list;
  }, [teamMembers, currentUser, teamRoomId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return targets;
    return targets.filter((t) => t.label.toLowerCase().includes(q));
  }, [targets, query]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => {
    setQuery('');
    setSelected(new Set());
    setIsSending(false);
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleForward = async () => {
    if (!message || !chat || selected.size === 0) return;
    setIsSending(true);

    const senderName = message.sender?.full_name || message.sender?.email || 'Someone';
    const sourceMeta = typeof message.metadata === 'string'
      ? (() => { try { return JSON.parse(message.metadata); } catch { return {}; } })()
      : (message.metadata || {});
    const { replyTo, ...rest } = sourceMeta || {};
    const forwardMeta = {
      ...rest,
      forwarded: {
        originalSenderId: message.sender_id,
        originalSenderName: senderName,
      },
    };

    let okCount = 0;
    const failures: string[] = [];

    for (const id of selected) {
      const target = targets.find((t) => t.id === id);
      if (!target) continue;
      try {
        if (target.kind === 'team-room') {
          const ok = await chat.forwardMessage(
            target.roomId,
            message.content,
            message.message_type || 'text',
            forwardMeta,
          );
          if (ok) okCount++; else failures.push(target.label);
        } else {
          await chat.sendDirectMessage(
            target.userId,
            message.content,
            message.message_type || 'text',
            forwardMeta,
          );
          okCount++;
        }
      } catch {
        failures.push(target.label);
      }
    }

    setIsSending(false);
    if (okCount > 0) {
      toast({ variant: "success",
        title: `Forwarded to ${okCount} ${okCount === 1 ? 'chat' : 'chats'}`,
        description: failures.length ? `Failed: ${failures.join(', ')}` : undefined,
      });
    } else {
      toast({
        title: 'Forward failed',
        description: 'No messages were sent. Please try again.',
        variant: 'destructive',
      });
    }

    reset();
    onOpenChange(false);
  };

  const preview = message?.content || '';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Forward className="h-4 w-4" />
            Forward message
          </DialogTitle>
          <DialogDescription>
            Select chats to forward this message to.
          </DialogDescription>
        </DialogHeader>

        {preview && (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground line-clamp-2">
            {preview}
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search team or members…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
          {filtered.length === 0 && (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">
              No chats match your search.
            </div>
          )}
          {filtered.map((t) => {
            const isSelected = selected.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted transition-colors ${
                  isSelected ? 'bg-muted' : ''
                }`}
              >
                {t.kind === 'team-room' ? (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center">
                    <Hash className="h-4 w-4" />
                  </div>
                ) : (
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={t.avatarUrl} />
                    <AvatarFallback className="text-xs">
                      {getInitials(t.label)}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.label}</div>
                  {t.kind === 'dm' && t.email && (
                    <div className="text-xs text-muted-foreground truncate">{t.email}</div>
                  )}
                </div>
                {isSelected && <Check className="h-4 w-4 text-primary" />}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button
            onClick={handleForward}
            disabled={selected.size === 0 || isSending}
          >
            {isSending
              ? 'Forwarding…'
              : `Forward${selected.size ? ` (${selected.size})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
